/**
 * VERA Dashboard Page
 * Overview of all cases with summary statistics, matching LensLock portal design
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Badge,
  Button,
  Divider,
} from "@fluentui/react-components";
import {
  Folder24Regular,
  ArrowRight16Regular,
  DocumentBulletList24Regular,
  Cube24Regular,
  Image24Regular,
  Video24Regular,
  Shield24Filled,
  Clock24Regular,
} from "@fluentui/react-icons";

import { useAuth } from "../auth/AuthProvider";
import { mockCases, mockEvidence } from "../data/mockData";
import type { CaseStatus, EvidenceCategory } from "../types";

const statusConfig: Record<
  CaseStatus,
  { label: string; color: "important" | "warning" | "success" | "informative" }
> = {
  active: { label: "Active", color: "important" },
  "under-review": { label: "Under Review", color: "warning" },
  closed: { label: "Closed", color: "success" },
  archived: { label: "Archived", color: "informative" },
};

const categoryIcons: Record<EvidenceCategory, React.ReactNode> = {
  "3d-scan": <Cube24Regular />,
  photograph: <Image24Regular />,
  video: <Video24Regular />,
  document: <DocumentBulletList24Regular />,
  audio: <DocumentBulletList24Regular />,
};

const useStyles = makeStyles({
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: tokens.spacingHorizontalL,
  },
  header: {
    marginBottom: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalL,
    borderBottom: "1px solid #e2e8f0",
  },
  greeting: {
    marginBottom: tokens.spacingVerticalS,
    color: "#004C97",
    fontFamily: "'Poppins', sans-serif",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "32px",
  },
  statCard: {
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  statIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #004C97 0%, #0066cc 100%)",
    color: "#ffffff",
    flexShrink: 0,
  },
  statInfo: {
    display: "flex",
    flexDirection: "column",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#2D2926",
    fontFamily: "'Poppins', sans-serif",
    lineHeight: "1",
  },
  statLabel: {
    fontSize: "12px",
    color: "#63666A",
    marginTop: "4px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#2D2926",
    fontFamily: "'Poppins', sans-serif",
    marginBottom: "16px",
  },
  casesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
    gap: "20px",
  },
  caseCard: {
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "24px",
    ":hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)",
    },
  },
  caseCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  caseIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #004C97 0%, #0066cc 100%)",
    color: "#ffffff",
    flexShrink: 0,
  },
  caseNumber: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#004C97",
    fontFamily: "'Poppins', sans-serif",
  },
  caseTitle: {
    fontSize: "15px",
    fontWeight: 600,
    marginTop: "4px",
    color: "#2D2926",
  },
  caseDescription: {
    fontSize: "13px",
    color: "#63666A",
    marginTop: "8px",
    lineHeight: "1.5",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  caseMeta: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "16px",
    paddingTop: "12px",
    borderTop: "1px solid #e2e8f0",
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: "#63666A",
  },
  caseAction: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalM,
  },
  evidenceSummary: {
    display: "flex",
    gap: "8px",
    marginTop: "12px",
    flexWrap: "wrap",
  },
  evidenceTag: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: "#004C97",
    backgroundColor: "rgba(0, 76, 151, 0.08)",
    padding: "3px 8px",
    borderRadius: "4px",
    fontWeight: 500,
  },
});

export const DashboardPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { displayName } = useAuth();

  const firstName = displayName?.split(" ")[0] || "there";

  // Calculate stats
  const totalCases = mockCases.length;
  const activeCases = mockCases.filter(
    (c) => c.status === "active" || c.status === "under-review"
  ).length;
  const totalEvidence = mockEvidence.length;
  const total3DScans = mockEvidence.filter(
    (e) => e.category === "3d-scan"
  ).length;

  // Get evidence category counts per case
  const getCategoryCounts = (caseId: string) => {
    const items = mockEvidence.filter((e) => e.caseId === caseId);
    const counts: Partial<Record<EvidenceCategory, number>> = {};
    items.forEach((item) => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    return counts;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Text size={800} weight="semibold" className={styles.greeting} block>
          Welcome, {firstName}
        </Text>
        <Text size={400} style={{ color: tokens.colorNeutralForeground3 }}>
          Arroyo360 Evidence Portal — Select a case to view digital evidence
        </Text>
      </div>

      {/* Stats Row */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>
            <Folder24Regular />
          </div>
          <div className={styles.statInfo}>
            <Text className={styles.statValue}>{totalCases}</Text>
            <Text className={styles.statLabel}>Total Cases</Text>
          </div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "linear-gradient(135deg, #c4314b 0%, #e74c3c 100%)" }}>
            <Shield24Filled />
          </div>
          <div className={styles.statInfo}>
            <Text className={styles.statValue}>{activeCases}</Text>
            <Text className={styles.statLabel}>Active Cases</Text>
          </div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "linear-gradient(135deg, #0e7a0d 0%, #27ae60 100%)" }}>
            <Cube24Regular />
          </div>
          <div className={styles.statInfo}>
            <Text className={styles.statValue}>{total3DScans}</Text>
            <Text className={styles.statLabel}>3D Scans</Text>
          </div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "linear-gradient(135deg, #63666A 0%, #8a8d91 100%)" }}>
            <DocumentBulletList24Regular />
          </div>
          <div className={styles.statInfo}>
            <Text className={styles.statValue}>{totalEvidence}</Text>
            <Text className={styles.statLabel}>Evidence Items</Text>
          </div>
        </Card>
      </div>

      {/* Cases Grid */}
      <Text className={styles.sectionTitle}>All Cases</Text>
      <div className={styles.casesGrid}>
        {mockCases.map((c) => {
          const config = statusConfig[c.status];
          const categoryCounts = getCategoryCounts(c.id);

          return (
            <Card
              key={c.id}
              className={styles.caseCard}
              onClick={() => navigate(`/case/${c.id}`)}
            >
              <div className={styles.caseCardHeader}>
                <div>
                  <Text className={styles.caseNumber}>{c.caseNumber}</Text>
                  <Text className={styles.caseTitle} block>
                    {c.title}
                  </Text>
                </div>
                <Badge
                  size="medium"
                  appearance="filled"
                  color={config.color}
                >
                  {config.label}
                </Badge>
              </div>

              <Text className={styles.caseDescription}>
                {c.description}
              </Text>

              {/* Evidence type breakdown */}
              <div className={styles.evidenceSummary}>
                {Object.entries(categoryCounts).map(([cat, count]) => (
                  <div key={cat} className={styles.evidenceTag}>
                    {categoryIcons[cat as EvidenceCategory]}
                    {count} {cat === "3d-scan" ? "3D Scan" : cat}
                    {count !== undefined && count > 1 ? "s" : ""}
                  </div>
                ))}
              </div>

              <div className={styles.caseMeta}>
                <div className={styles.metaItem}>
                  <Shield24Filled style={{ fontSize: "14px" }} />
                  {c.assignedOfficer}
                </div>
                <div className={styles.metaItem}>
                  <Clock24Regular style={{ fontSize: "14px" }} />
                  {new Date(c.incidentDate).toLocaleDateString()}
                </div>
              </div>

              <div className={styles.caseAction}>
                <Button appearance="transparent" size="small">
                  View Evidence
                  <ArrowRight16Regular />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardPage;
